'use client';

import { motion } from 'framer-motion';

import {
  Badge,
  MENU_ACTIVE_ROW_CLASS,
  MENU_ROW_CLASS,
  MENU_SECTION_LABEL_CLASS,
} from '../../lib/header-constants';
import { getMegaMenuLinkBadgeColor } from '../../lib/mega-menu-badge';
import { cn } from '@/shared/utils/cn';
import { useNavbarContext } from './context';
import type { NavMegaMenuGroup } from '../../types/navigation';

interface HeaderMegaMenuPanelProps {
  activeGroup?: NavMegaMenuGroup;
}

const MEGA_MENU_PANEL_INITIAL_STATE = { opacity: 0, y: 4 };
const MEGA_MENU_PANEL_ANIMATE_STATE = { opacity: 1, y: 0 };
const MEGA_MENU_PANEL_TRANSITION = {
  duration: 0.18,
  ease: [0.16, 1, 0.3, 1],
} as const;

const HeaderMegaMenuPanel = ({ activeGroup }: HeaderMegaMenuPanelProps) => {
  const { onNavClick } = useNavbarContext();

  if (!activeGroup) {
    return null;
  }

  return (
    <div className="flex min-h-0 flex-col px-4 py-3">
      <motion.div
        animate={MEGA_MENU_PANEL_ANIMATE_STATE}
        className="flex h-full flex-col"
        initial={MEGA_MENU_PANEL_INITIAL_STATE}
        key={activeGroup.id}
        transition={MEGA_MENU_PANEL_TRANSITION}
      >
        <p className={MENU_SECTION_LABEL_CLASS}>{activeGroup.label}</p>

        <ul className="mt-2 space-y-0.5">
          {activeGroup.links.map((link) => (
            <li key={link.label}>
              <a
                className={cn('flex items-center gap-2', MENU_ROW_CLASS)}
                href={link.href}
                onClick={() => onNavClick?.({ href: link.href, label: link.label, url: link.href })}
              >
                <span>{link.label}</span>
                {link.badge ? (
                  <Badge color={getMegaMenuLinkBadgeColor(link)} label={link.badge} />
                ) : null}
              </a>
            </li>
          ))}
        </ul>

        {activeGroup.secondaryAction ? (
          <div className="border-adipa-border mt-3 border-t pt-2">
            <a
              className={cn('block', MENU_ROW_CLASS, MENU_ACTIVE_ROW_CLASS)}
              href={activeGroup.secondaryAction.href}
              onClick={() =>
                onNavClick?.({
                  href: activeGroup.secondaryAction!.href,
                  label: activeGroup.secondaryAction!.label,
                  url: activeGroup.secondaryAction!.href,
                })
              }
            >
              {activeGroup.secondaryAction.label}
            </a>
          </div>
        ) : null}
      </motion.div>
    </div>
  );
};

export default HeaderMegaMenuPanel;
